<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import PurchaseProjectCard from './PurchaseProjectCard.vue';
import NewPurchaseProjectDrawer from './NewPurchaseProjectDrawer.vue';
import PurchaseProjectDetail from './PurchaseProjectDetail.vue';
import { usePurchaseProjectsState } from './usePurchaseProjectsState.js';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
  contactName: {
    type: String,
    default: '',
  },
  contactPhone: {
    type: String,
    default: '',
  },
  contactEmail: {
    type: String,
    default: '',
  },
  contactPreferredChannel: {
    type: String,
    default: 'whatsapp',
  },
});

const emit = defineEmits([
  'closeSheet',
  'projectCreated',
  'projectUpdated',
  'returnToCloseCircle',
]);

const { t } = useI18n();
const {
  purchaseProjectsForContact,
  loadPurchaseProjects,
  updatePurchaseProject,
} = usePurchaseProjectsState();

const projects = computed(() => purchaseProjectsForContact(props.contactId));

const newProjectDrawerRef = ref(null);
const selectedProject = ref(null);
// 'direct' = opened from contact view (no list behind), 'list' = navigated from list
const openedFrom = ref('list');
const returnContext = ref(null);

const hasSelectedProject = computed(() => !!selectedProject.value);

const loadProjects = () => loadPurchaseProjects(props.contactId);

onMounted(loadProjects);
watch(() => props.contactId, loadProjects);

const openNewProject = () => {
  newProjectDrawerRef.value?.open();
};

const onProjectCreated = project => {
  openedFrom.value = 'list';
  returnContext.value = null;
  selectedProject.value = project;
  emit('projectCreated', project);
};

const openProject = (project, options = {}) => {
  openedFrom.value = options.source ?? 'direct';
  returnContext.value = options.returnContext ?? null;
  selectedProject.value = project;
};

const openProjectFromList = project => {
  openedFrom.value = 'list';
  returnContext.value = null;
  selectedProject.value = project;
};

const closeDetail = () => {
  selectedProject.value = null;
  if (openedFrom.value === 'closeCircle') {
    emit('returnToCloseCircle', returnContext.value);
    returnContext.value = null;
    return;
  }
  if (openedFrom.value === 'direct') {
    emit('closeSheet');
  }
};

const requestClose = () => {
  if (selectedProject.value && openedFrom.value === 'closeCircle') {
    closeDetail();
    return;
  }
  emit('closeSheet');
};

const addProject = () => {
  loadPurchaseProjects(props.contactId, { force: true });
};

const showList = () => {
  selectedProject.value = null;
  returnContext.value = null;
};

defineExpose({
  openNewProject,
  addProject,
  openProject,
  hasSelectedProject,
  showList,
  requestClose,
});

const onProjectUpdated = async updated => {
  const persisted = await updatePurchaseProject(updated.id, updated);
  selectedProject.value = persisted ?? updated;
  emit('projectUpdated', selectedProject.value);
};
</script>

<template>
  <div class="flex flex-col gap-0">
    <PurchaseProjectDetail
      v-if="selectedProject"
      :project="selectedProject"
      :contact-id="contactId"
      :contact-name="contactName"
      :contact-phone="contactPhone"
      :contact-email="contactEmail"
      :contact-preferred-channel="contactPreferredChannel"
      @back="closeDetail"
      @updated="onProjectUpdated"
    />

    <template v-else>
      <div class="flex items-center justify-between px-4 pt-4 pb-3">
        <div class="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            class="!rounded-full bg-n-alpha-black2 shrink-0"
            @click="emit('closeSheet')"
          />
          <span class="text-base font-semibold text-n-slate-12 truncate">
            {{ t('PURCHASE_PROJECTS.ALL_PROJECTS_TITLE') }}
          </span>
        </div>
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          class="!rounded-full bg-n-alpha-black2 shrink-0"
          @click="openNewProject"
        />
      </div>

      <div v-if="projects.length > 0" class="flex flex-col">
        <template v-for="(project, index) in projects" :key="project.id">
          <div v-if="index > 0" class="mx-4 h-px bg-n-weak" />
          <PurchaseProjectCard
            :project="project"
            compact
            @open="openProjectFromList"
          />
        </template>
      </div>

      <p v-else class="px-6 py-8 text-sm text-center text-n-slate-11">
        {{ t('PURCHASE_PROJECTS.EMPTY_STATE') }}
      </p>
    </template>

    <NewPurchaseProjectDrawer
      ref="newProjectDrawerRef"
      :contact-id="contactId"
      :contact-name="contactName"
      :contact-phone="contactPhone"
      :contact-email="contactEmail"
      @created="onProjectCreated"
    />
  </div>
</template>
